// Scroll-driven collapsing header with progressive glass effect
//
// Animates title appearance and glass effect based on scroll position.
// Desktop: static with full effect. Mobile: smooth scroll-driven transitions.
//
// Tweakable constants:
//   TITLE_SLIDE_AMPLITUDE - vertical slide distance (px)
//   EASE_EXPONENT - easing curve (higher = more ease-out)
//   GLASS_ZONE_MULTIPLIER - glass effect transition zone size (multiplier of padding)
//   DISPLAY_TITLE_OFFSET - offset for title visibility threshold (px)

import { Controller } from "@hotwired/stimulus"

const DESKTOP_QUERY = "(min-width: 750px)"
const DISPLAY_TITLE_OFFSET = 34
const TITLE_SLIDE_AMPLITUDE = 5
const EASE_EXPONENT = 3
const GLASS_ZONE_MULTIPLIER = 2

export default class extends Controller {
  static targets = ["mainHeader", "secondaryHeader", "secondaryTitle"]

  connect() {
    if (!this.hasMainHeaderTarget || !this.hasSecondaryHeaderTarget || !this.hasSecondaryTitleTarget) return

    this.mainClassList = this.mainHeaderTarget.classList
    this.rootClassList = document.documentElement.classList
    this.root = document.documentElement

    this.rafId = 0
    this.onScroll = () => this.scheduleUpdate()
    this.onTurboSubmit = () => this.disconnect()

    this.mql = window.matchMedia(DESKTOP_QUERY)
    this.onMediaChange = () => this.applyMode()
    this.mql.addEventListener("change", this.onMediaChange)
    document.addEventListener("turbo:submit-start", this.onTurboSubmit)

    this.applyMode()
  }

  disconnect() {
    this.resetState()
    this.mql?.removeEventListener("change", this.onMediaChange)
    document.removeEventListener("turbo:submit-start", this.onTurboSubmit)
    this.resizeObserver?.disconnect()
  }

  applyMode() {
    this.resetState()

    if (this.mql.matches) {
      this.initDesktop()
    } else {
      this.initMobile()
    }
  }

  initDesktop() {
    this.toggle("displayTitle", "has-display-title", true)
    this.toggle("borderBottom", "has-border-bottom", true)
    this.mainHeaderTarget.style.setProperty("--PageHeader-Title-Progress", 1)
    this.mainHeaderTarget.style.setProperty("--PageHeader-Glass-Progress", 1)
    this.mainHeaderTarget.style.setProperty("--PageHeader-Title-SlideAmplitude", `${TITLE_SLIDE_AMPLITUDE}px`)
  }

  initMobile() {
    this.calcThreshold()

    window.addEventListener("scroll", this.onScroll, { passive: true })

    this.resizeObserver = new ResizeObserver(() => this.calcThreshold())
    this.resizeObserver.observe(this.secondaryTitleTarget)

    this.update()
  }

  calcThreshold() {
    this.mainPaddingBottom = parseFloat(getComputedStyle(this.mainHeaderTarget).paddingBottom) || 0
    const titleHeight = this.secondaryTitleTarget.getBoundingClientRect().height
    this.displayTitleThreshold = titleHeight * 2 + this.mainPaddingBottom * 2 - DISPLAY_TITLE_OFFSET
  }

  scheduleUpdate() {
    if (this.rafId) return
    this.rafId = requestAnimationFrame(() => {
      this.rafId = 0
      this.update()
    })
  }

  update() {
    const headerRect = this.secondaryHeaderTarget.getBoundingClientRect()
    const titleRect = this.secondaryTitleTarget.getBoundingClientRect()
    const mainPaddingBottom = parseFloat(getComputedStyle(this.mainHeaderTarget).paddingBottom) || 0

    const end = this.displayTitleThreshold
    const start = end + titleRect.height
    const progress = Math.min(1, Math.max(0, (start - titleRect.bottom) / (start - end)))

    const glassProgress = Math.min(1, Math.max(0, (mainPaddingBottom - headerRect.top) / (mainPaddingBottom * GLASS_ZONE_MULTIPLIER)))

    this.toggle("displayTitle", "has-display-title", progress >= 1)
    this.toggle("borderBottom", "has-border-bottom", headerRect.top < mainPaddingBottom)

    const eased = 1 - Math.pow(1 - progress, EASE_EXPONENT)
    this.mainHeaderTarget.style.setProperty("--PageHeader-Title-Progress", eased)
    this.mainHeaderTarget.style.setProperty("--PageHeader-Glass-Progress", glassProgress)
    this.secondaryHeaderTarget.style.setProperty("--PageHeader-Hero-Progress", 1 - eased)
  }

  resetState() {
    this.state = { displayTitle: undefined, borderBottom: undefined }
    this.mainHeaderTarget.classList.remove("has-display-title", "has-border-bottom")
    this.mainHeaderTarget.style.removeProperty("--PageHeader-Title-Progress")
    this.mainHeaderTarget.style.removeProperty("--PageHeader-Glass-Progress")
    this.mainHeaderTarget.style.removeProperty("--PageHeader-Title-SlideAmplitude")
    this.secondaryHeaderTarget.style.removeProperty("--PageHeader-Hero-Progress")

    window.removeEventListener("scroll", this.onScroll)
    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = 0
    }
  }

  toggle(key, className, value) {
    if (this.state[key] === value) return

    this.state[key] = value
    this.mainHeaderTarget.classList.toggle(className, value)
  }
}
